/**
 * The stock structure as a tree, with the totals at every level.
 *
 * Two readings of the same data, because the business asks both questions:
 *
 * - **By company** — what each company of the group holds, and where. A place
 *   hosting the depots of three companies appears under each of the three,
 *   with only that company's depots beneath it. The place is shared; the
 *   stock is not.
 * - **By point of sale** — what is physically in a place, whoever owns it.
 *
 * Rendered as a table with indented, expandable rows rather than a nested
 * list, so the figures line up in columns — the comparison an operator makes
 * is between two places' "disponible", not within one.
 *
 * Depots with no company or no place sit under a "sans société" / "sans
 * point de vente" row rather than disappearing: stock missing from the tree
 * is stock missing from the totals.
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useResource } from "@hardauto/core-data";
import { quantity } from "@hardauto/core-i18n";
import { Button, DataTable, Select, Toolbar, type Column } from "@hardauto/core-ui";

import type { ReferentialApi, StructureNode, StructureView } from "../../api";
import { NAMESPACE } from "../../i18n";
import { flatten, type TreeRow } from "./tree";

export interface StructureTreeViewProps {
  api: ReferentialApi;
}

/** Remounted by its parent (a `key`) after an import, to read the tree again. */
export function StructureTreeView({ api }: StructureTreeViewProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const [view, setView] = useState<StructureView>("company");
  // Paths of the open nodes. The heads start open: the first thing anybody
  // wants is the second level, not a list of three company names.
  const [open, setOpen] = useState<ReadonlySet<string> | null>(null);

  const fetcher = useCallback(() => api.stockStructure(view), [api, view]);
  const tree = useResource(fetcher);
  const items = tree.data?.items ?? [];
  const expanded = open ?? new Set(items.map((node) => `${node.level}:${node.code}`));
  const rows = flatten(items, expanded);

  function toggle(path: string) {
    const next = new Set(expanded);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setOpen(next);
  }

  function label(node: StructureNode): string {
    if (node.code) return node.name || node.code;
    return node.level === "company" ? t("structure.tree.noCompany") : t("structure.tree.noPoint");
  }

  const figure = (value: string) => (
    <span className="hardauto-num">{quantity(value, i18n.language)}</span>
  );

  const columns: Column<TreeRow>[] = [
    {
      key: "name",
      header: t("structure.tree.column.name"),
      render: ({ node, depth, path, hasChildren, isOpen }) => (
        <div
          className="hardauto-structure__node"
          style={{ paddingInlineStart: `${depth * 1.5}rem` }}
        >
          {hasChildren ? (
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={isOpen}
              aria-label={
                isOpen
                  ? t("structure.tree.collapse", { name: label(node) })
                  : t("structure.tree.expand", { name: label(node) })
              }
              onClick={() => toggle(path)}
            >
              {isOpen ? "▾" : "▸"}
            </Button>
          ) : (
            <span className="hardauto-structure__leaf" aria-hidden="true" />
          )}
          <span data-level={node.level} className="hardauto-structure__label">
            {node.level === "depot" ? <code>{node.code}</code> : null} {label(node)}
            {node.city ? <span className="hardauto-structure__city"> · {node.city}</span> : null}
          </span>
        </div>
      ),
    },
    {
      key: "depots",
      header: t("structure.tree.column.depots"),
      render: (row) => row.node.depot_count,
      align: "end",
    },
    {
      key: "lines",
      header: t("structure.tree.column.lines"),
      render: (row) => row.node.lines,
      align: "end",
    },
    {
      key: "real",
      header: t("structure.tree.column.real"),
      render: (row) => figure(row.node.real),
      align: "end",
    },
    {
      key: "reserved",
      header: t("structure.tree.column.reserved"),
      render: (row) => figure(row.node.reserved),
      align: "end",
      secondary: true,
    },
    {
      key: "available",
      header: t("structure.tree.column.available"),
      render: (row) => figure(row.node.available),
      align: "end",
    },
  ];

  return (
    <div className="hardauto-structure__panel">
      <Toolbar>
        <Select
          aria-label={t("structure.tree.viewLabel")}
          value={view}
          onChange={(event) => {
            setView(event.target.value as StructureView);
            setOpen(null);
          }}
        >
          <option value="company">{t("structure.tree.byCompany")}</option>
          <option value="point_of_sale">{t("structure.tree.byPoint")}</option>
        </Select>
      </Toolbar>
      <DataTable<TreeRow>
        caption={t("structure.tree.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.path}
        loading={tree.loading}
        loadingLabel={t("structure.tree.loading")}
        error={
          tree.failed
            ? { title: t("error.title"), onRetry: tree.reload, retryLabel: t("error.retry") }
            : undefined
        }
        empty={{
          title: t("structure.tree.empty.title"),
          description: t("structure.tree.empty.body"),
        }}
      />
    </div>
  );
}
